import { Box, Icon, IconNew, Loader, Spacer, SpacerNew, Text, TextNew } from '@jfs/ui-new'
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/core'
import { FC, useCallback, useEffect } from 'react'
import { Dimensions, ScrollView } from 'react-native'
import { ReportFormatEnum } from '../../../../src/data/models/Settlements'
import { PaginationController } from '../../../../src/screens/PaymentLinks/PaginationController/PaginationController'
import EmptyView from '../../../comps/EmptyView'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import { NewTable } from '../../../comps/NewTable'
import { settlementReportsTitleData } from '../../../data/static/data'
import { transactionScreenStrings } from '../../../data/static/strings'
import { useLazyApiData, useStores } from '../../../data/store'
import { Routes } from '../../../nav/routes'
import { getCustomColumnWidth, getErrorMessage, getLoadingState, pages, useWindowDimensions } from '../../../util/util'
import styles from '../styles'

const SettlementsReporting: FC = () => {
  const {
    fetchSettlementReportsFormatListApiList,
    pageNum,
    paginationData,
    mid,
    getSettlementReportsData,
    deleteSettlementReportFormat,
    updatePagination,
  } = useStores((store) => ({
    fetchSettlementReportsFormatListApiList: store.settlements.fetchSettlementReportsFormatListApiList,
    pageNum: store.reports.pagination.number,
    paginationData: store.reports.pagination,
    updatePagination: store.reports.updatePagination,
    mid: store.user.selectedMid,
    getSettlementReportsData: store.settlements.getSettlementReportsData,
    deleteSettlementReportFormat: store.settlements.deleteSettlementReportFormat,
  }))
  const themes = styles(0)
  const path = useRoute().name
  const navigation = useNavigation()

  const [fetchSettlementReportsFormatListApi, fetchSetlementsReportsListHandler] = useLazyApiData(
    fetchSettlementReportsFormatListApiList,
  )

  const [deleteSettlementReportFormatApi, deleteSettlementReportFormatHandler] =
    useLazyApiData(deleteSettlementReportFormat)

  const apis = [fetchSetlementsReportsListHandler]
  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)

  useFocusEffect(
    useCallback(() => {
      fetchSettlementReportsFormatListApi(mid, pageNum)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchSettlementReportsFormatListApi, mid]),
  )

  useEffect(() => {
    if (deleteSettlementReportFormatHandler?.state === 'fulfilled') {
      fetchSettlementReportsFormatListApi(mid, pageNum) // get call list API
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSettlementReportFormatHandler.state, fetchSettlementReportsFormatListApi, mid])

  const handlePagination = (selectedPage: number) => {
    updatePagination(selectedPage)
    const pageNumber = paginationData.number === 0 ? 1 : paginationData.number
    fetchSettlementReportsFormatListApi(mid, pageNumber)
  }

  let listData = getSettlementReportsData()
  listData = JSON.parse(JSON.stringify(listData))

  const headerLength = Object.keys(settlementReportsTitleData).length
  for (let i = 0; i < listData.length; i++) {
    const reportColumn = Object.keys(listData[i])
    if (reportColumn.length !== headerLength) {
      if (listData[i].reportFormat === ReportFormatEnum.STANDARD) {
        listData[i].reportFormat = 'Standard'
      } else if (listData[i].reportFormat === ReportFormatEnum.CUSTOM) {
        listData[i].reportFormat = 'Custom'
      } else if (listData[i].reportFormat === ReportFormatEnum.CUSTOM_RRL) {
        listData[i].reportFormat = 'Custom RRL'
      }
      listData[i].actionEditDownload = 'actionEditDownload'
      listData[i].tableIndex = paginationData.number > 1 ? paginationData?.number * 10 + i + 1 - 10 : i + 1
    }
  }

  const getEmptyMessage = (): string => {
    return listData?.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
  }

  const onItemDelete = (data: any) => {
    deleteSettlementReportFormatApi(data.requestId, mid)
  }

  const viewPortWidth = useWindowDimensions()

  const scrollViewStyle = () => {
    if (listData.length === 0) {
      return themes['settlements-reporting'].emptyContainer
    }
    return themes['settlements-reporting'].tableContainer(loadingState)
  }
  const scrollStyle = scrollViewStyle()

  const onCreateReportClicked = () => {
    navigation.navigate(Routes.SETTLEMENTS_ADD_UPDATE_REPORT as never)
  }

  const handleRetry = () => {
    if (mid.length > 0) {
      fetchSettlementReportsFormatListApi(pageNum, mid)
    }
  }

  const onEditReport = (data: any) => {
    navigation.navigate(Routes.SETTLEMENTS_ADD_UPDATE_REPORT as never, { data: data.id } as never)
  }
  return (
    <ScrollView contentContainerStyle={themes.scrollContainer}>
      <Box {...themes[path].container} justifyContent={'flex-start'}>
        <Box {...themes.stickyHeader} flexDirection={'row'} justifyContent={'space-between'}>
          <Box justify="center">
            <TextNew fontFamily="jiotype-medium" type={'bodySecondary18'} fontSize={21} color={'BLACK_21'}>
              {'Saved Configurations'}
            </TextNew>
            <Box alignContent={'center'} mt={10} flexDirection="row">
              <Box
                underlayColor={'TRANSPARENT'}
                testID="settlement-nav"
                onPress={() => navigation.canGoBack() && navigation.goBack()}>
                <TextNew fontSize={'FONT_SIZE_SMALL'} color={'BLUE'}>
                  Settlements
                </TextNew>
              </Box>
              <SpacerNew size={7} direction="horizontal" />
              <IconNew id={'ARROW_RIGHT'} size={8} color={'PRIMARY_GREY'} />
              <SpacerNew size={7} direction="horizontal" />
              <TextNew fontSize={'FONT_SIZE_SMALL'} color={'PRIMARY_GREY'}>
                Saved Configurations
              </TextNew>
            </Box>
          </Box>

          <ModBox show={path === Routes.SETTLEMENTS_REPORTING && listData.length > 0} flexDirection="row">
            <PaginationController
              hide={false}
              currentPage={paginationData?.number ?? 0}
              totalPages={paginationData?.totalPages ?? 0}
              totalDataLength={paginationData?.totalElements ?? 0}
              dataSetSize={paginationData?.size ?? 0}
              onSelect={handlePagination}
              isZeroIndex={false}
              defaultPage={1}
            />
            {/* <ActionsMenu
              path={'settlements-reporting'}
              fieldSettingVisible={false}
              setFieldSettingVisible={() => null}
              filterVisible={false}
              setFilterVisible={() => null}
            /> */}
            <Spacer size={10} direction="horizontal" />
            <ModBox
              {...themes.addReportButton}
              testID="add-new-config"
              onPress={onCreateReportClicked}
              underlayColor={'ALTO_DISABLED'}>
              <Text fontFamily="jiotype-medium" color="WHITE" fontSize={12}>
                {'+ Add New Configuration'}
              </Text>
            </ModBox>
          </ModBox>
        </Box>
        <Spacer size={30} direction="vertical" />
        <EmptyView
          onRetry={handleRetry}
          isLoading={loadingState}
          error={errorState}
          emptyMessage={getEmptyMessage()}
          loadingMessage={'Loading...'}
          hide={!loadingState && !errorState}
        />
        <ModBox show={deleteSettlementReportFormatHandler.state === 'pending'} margin={16}>
          <Loader color="PRIMARY" />
        </ModBox>
        <ModBox
          alignItems={'center'}
          justifyContent={'center'}
          flexGrow={1}
          show={listData?.length === 0 && !loadingState && !errorState}>
          <Icon id="ACCOUNT" size={56} color="PRIMARY" />
          <Spacer size={38} direction="vertical" />
          <Text
            title={pages.settlement_reporting.reports_not_configured}
            fontSize={20}
            fontFamily="jiotype-bold"
            color={'BLACK'}
          />
          <Spacer size={16} direction="vertical" />
          <Box maxWidth={Dimensions.get('screen').width / 3}>
            <Text
              title={pages.settlement_reporting.no_config_discription}
              fontSize={'SMALL'}
              fontFamily="jiotype-medium"
              color={'DARK_GREY'}
              textAlign="center"
            />
          </Box>
          <Spacer size={60} direction="vertical" />
          <ModBox
            {...themes.addReportButton}
            testID="configure-now"
            onPress={onCreateReportClicked}
            underlayColor={'ALTO_DISABLED'}>
            <Text fontFamily="jiotype-medium" color="WHITE" fontSize={12}>
              {pages.settlement_reporting.configure_now}
            </Text>
          </ModBox>
          {/* <Spacer size={32} direction="vertical" />
          <Text fontFamily="jiotype-medium" color="PRIMARY" fontSize={16} fontStyle="italic">
            {'Know more...'}
          </Text> */}
        </ModBox>
        <ModBox show={!loadingState && !errorState && !getEmptyMessage() && listData?.length > 0}>
          <ScrollView scrollEnabled={true} horizontal contentContainerStyle={{ ...scrollStyle, height: '70vh' }}>
            <Box width={'100%'} overflow={'auto'} nativeID="scroll-horizontal-Table">
              <NewTable
                data={listData}
                rowHeight={48}
                headerHeight={64}
                customWidthFunc={getCustomColumnWidth}
                height={346}
                tableWidth={viewPortWidth > 858 ? viewPortWidth - 290 : viewPortWidth - 40}
                headerRowKeys={settlementReportsTitleData}
                onRowClick={() => null}
                onAction={onEditReport}
                onEditConfig={onEditReport}
                onDeleteReportFile={onItemDelete}
              />
            </Box>
          </ScrollView>
        </ModBox>
      </Box>
    </ScrollView>
  )
}

export { SettlementsReporting as default }
